/**
 * Google Analytics Event Tracking Utility
 * 
 * This utility provides a centralized way to track user interactions
 * with buttons and UI elements throughout the application.
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * Track a Google Analytics event
 * @param action - The action being performed (e.g., 'click', 'send', 'receive')
 * @param category - The category of the event (e.g., 'button', 'navigation', 'message')
 * @param label - Optional label for additional context (e.g., button name, page name)
 * @param value - Optional numeric value
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

/**
 * Track button clicks
 */
export const trackButtonClick = (buttonName: string, location?: string): void => {
  trackEvent('click', 'button', `${buttonName}${location ? ` - ${location}` : ''}`);
};

/**
 * Track navigation events
 */
export const trackNavigation = (destination: string, source?: string): void => {
  trackEvent('navigate', 'navigation', `${source ? `${source} -> ` : ''}${destination}`);
};

/**
 * Track message actions
 */
export const trackMessageAction = (action: string, messageType?: string): void => {
  trackEvent(action, 'message', messageType);
};

/**
 * Track device actions
 */
export const trackDeviceAction = (action: string, deviceType?: string): void => {
  trackEvent(action, 'device', deviceType);
};

/**
 * Track authentication events
 */
export const trackAuthEvent = (action: string, method?: string): void => {
  trackEvent(action, 'authentication', method);
};

/**
 * Track external message events
 */
export const trackExternalMessage = (action: string, type?: string): void => {
  trackEvent(action, 'external_message', type);
};

